import { forwardRef } from 'react';

import { cn } from '../../utils/utils';

export interface OptionTileProps {
  title: string;
  subtitle?: string;
  selected: boolean;
  onSelect: () => void;
  /** Roving tabindex, owned by the parent `TileRadioGroup`. */
  tabIndex?: number;
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
}

/**
 * One selectable tile in a single-choice grid: a radio dot, a title, and an
 * optional line of supporting copy.
 *
 * `role='radio'`, not a button with `aria-pressed`. These grids are
 * single-choice - picking one unpicks the others - and `aria-pressed` models
 * independent toggles, so a screen reader announced "pressed" on ten unrelated
 * buttons and offered no way to move between them. As a radio it announces
 * "selected, 2 of 4" and gets arrow-key navigation. It therefore MUST be
 * rendered inside a `role='radiogroup'` (axe enforces the parent) - always
 * reach for `TileRadioGroup` rather than mounting tiles directly.
 *
 * **The tile carries no border of its own.** Ten bordered boxes of identical
 * weight is what made the onboarding grids read as a form: every option drew
 * its own rectangle, so nothing led and the chosen one had to win against nine
 * competing edges with a 1px colour change. An unselected option sits on a soft
 * `muted` wash instead, so the only ring on the surface belongs to the answer.
 * The radio dot is both the affordance and the selected state, which is why
 * there is no corner check badge either.
 */
export const OptionTile = forwardRef<HTMLButtonElement, OptionTileProps>(
  function OptionTile(
    { title, subtitle, selected, onSelect, tabIndex, onKeyDown },
    ref
  ) {
    return (
      <button
        ref={ref}
        type='button'
        role='radio'
        aria-checked={selected}
        tabIndex={tabIndex}
        onClick={onSelect}
        onKeyDown={onKeyDown}
        className={cn(
          'duration-fast flex h-full gap-3 rounded-lg p-4 text-left transition-colors',
          // `rounded-lg` because tiles nest inside a larger `rounded-2xl`
          // surface (the onboarding card, the Settings card): nested corners
          // step DOWN, per Foundation/Radius.
          'focus-visible:ring-ring focus-visible:ring-offset-background outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          selected
            ? 'bg-brand-500/10 ring-brand-600 dark:bg-brand-400/10 dark:ring-brand-400 ring-1'
            : 'bg-muted/40 hover:bg-muted/70 dark:bg-muted/30 dark:hover:bg-muted/50'
        )}
      >
        <RadioDot selected={selected} />
        <span className='flex min-w-0 flex-col'>
          <span className='text-body text-foreground font-semibold'>
            {title}
          </span>
          {subtitle && (
            <span className='text-label text-muted-foreground mt-0.5'>
              {subtitle}
            </span>
          )}
        </span>
      </button>
    );
  }
);

/**
 * The radio affordance, and the entire selected state.
 *
 * Drawn rather than using an `<input type='radio'>`: the accessible role is
 * already carried by the button above, and a native input would either need
 * hiding (a second focus target) or restyling past what the platform allows.
 */
function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden='true'
      className={cn(
        'duration-fast mt-0.5 h-4 w-4 shrink-0 rounded-full border transition-colors',
        selected
          ? 'border-brand-600 dark:border-brand-400 border-[5px]'
          : 'border-muted-foreground/40'
      )}
    />
  );
}
